import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useItemFilters } from '../../../contexts/ItemFilterContext';
import { useMasters } from '../../../contexts/MastersContext';
import { useBiddingResultGetAllResultListQuery } from '../../../hooks/useBiddingResultQuery';
import { BiddingResultItemRow } from '../../../components/admin/BiddingResult/BiddingResultItemRow';
import { BiddingDisplayConditionConst } from '../../../constants/BiddingDisplayConditionConst';
import { UserRole } from '../../../types/UserRole';
import { UserPermission } from '../../../types/UserPermission';
import { useGetBiddingDateForResultQuery } from '../../../hooks/useCalendarQuery'; // 終了済の入札日取得用フック
import {
  CombinedCategoryOptions,
  ListingCategoryConst,
  parseCombinedCategory,
  PrefTypeConst,
} from '../../../constants'; // 合体セレクトボックス用の定数
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useGetFavoritesQuery } from '../../../hooks/useBiddingItemsQuery';
import { BiddingInputFilterDialog } from '../../../components/buyer/BiddingInput/BiddingInputFilterDialog';
import { useBiddingResultPdfUrlQuery } from '../../../hooks/useBiddingReportQuery';
import { RefreshCw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

//--------------------------------
// 落札結果確認のメインコンポーネント
//--------------------------------
export const BiddingResultPage = () => {

  //----------------
  // Context
  //----------------
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters();
  const { isMastersLoading, sellers, farmers, teaTypes, teaSeasons, varieties, gaps } = useMasters();

  // 終了済の入札日をAPIから取得
  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForResultQuery(currentUser?.loginId);

  //----------------
  // State
  //----------------
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // 初期化完了フラグ
  const [displayMode, setDisplayMode] = useState<'all' | 'favorites'>('all'); 
  const [isPrinting, setIsPrinting] = useState(false);
  const isActualSeller = currentUser?.role === UserRole.Seller; // 本物の売手（自動絞り込みの対象）
  const isJA = currentUser?.role === UserRole.JA;
  const isFarmer = currentUser?.role === UserRole.Farmer;
  const isBuyer = currentUser?.role === UserRole.Buyer;

  // 市場(Admin)かつ、権限が「編集者(1)」または「閲覧のみ(2)」かどうか
  const isAdmin = currentUser?.role === UserRole.Admin;
  const userPerm = Number(currentUser?.permission);
  const isAdminEditorOrViewer = isAdmin && (userPerm === UserPermission.Editor || userPerm === UserPermission.Viewer);

  // 画面のレイアウトを「売手用」にするかどうかの統合フラグ
  const isSellerLayout = isActualSeller || isAdminEditorOrViewer || isJA;

  //----------------
  // 初期値設定 (マウント時)
  //----------------
  useEffect(() => {
    // データの読み込み待機
    if (isDateLoading || isMastersLoading || !currentUser) return;
    if (isInitialized) return; // 二重実行防止

    // --- ① ログインユーザーからデフォルト地区（1:県内 or 2:県外）を特定 ---
    let defaultPref: number = PrefTypeConst.INTERNAL; 
      
    // 売手または生産者の場合、所属する売手(parentCode)の情報をマスタから探す
    const targetSellerCode = currentUser?.parentCode;
    // 本物の売手・生産者のみ適用（市場ユーザーは対象外）
    if ((isActualSeller || isFarmer) && targetSellerCode) {
      const myData = sellers.find(s => String(s.sellerCode) === String(targetSellerCode));
      if (myData) {
        // マスタで見つかった売手の県内/県外区分をデフォルトにする
        defaultPref = myData.prefectureType;
      }
    }
    
    // --- ② URLフィルターの初期化 ---
    const newFilters = { ...itemFilters };
    let hasChanged = false;

    // 入札日の初期設定 (APIからの日付 or 今日)
    if (!newFilters.biddingDate) {
      newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
      hasChanged = true;
    }
    // 出品区分の初期設定 (通常入札)
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }
    // 県内/県外区分の初期設定
    if (!newFilters.prefectureType) {
      newFilters.prefectureType = defaultPref;
      hasChanged = true;
    }
    // 表示対象の初期設定 (買い手の場合)
    if (isBuyer && !newFilters.biddingDisplayCondition) {
      newFilters.biddingDisplayCondition = BiddingDisplayConditionConst.BIDDING;
      hasChanged = true;
    }

    // 売手または生産者の場合、売手コードをセット
    // 本物の売手・生産者のみ適用（市場の編集者・閲覧者は絞り込み不要とするため対象外）
    if ((isActualSeller || isFarmer) && !newFilters.sellerCode) {
      newFilters.sellerCode = currentUser?.parentCode ? Number(currentUser.parentCode) : "";
      hasChanged = true;
    }

    // 生産者の場合のみ、生産者コードをセット
    if (isFarmer && !newFilters.farmerCode) {
      newFilters.farmerCode = currentUser?.childCode ? Number(currentUser.childCode) : "";
      hasChanged = true;
    }

    // 変更があればContext(URL)を更新
    if (hasChanged) {
      updateFiltersAndNavigate(newFilters);
    }

    setIsInitialized(true); // 初期化完了

  }, [
    initialBiddingDate, 
    isDateLoading, 
    isMastersLoading, 
    isActualSeller, 
    sellers, 
    currentUser, 
    itemFilters, 
    isInitialized, 
    updateFiltersAndNavigate, 
    isBuyer,
    isFarmer
  ]); // 依存配列を省略せずに全て記載

  //----------------
  // Memo / Data Fetching
  //----------------
  const masterDataForComponents = useMemo(() => ({
      varieties, teaTypes, teaSeasons, sellers, farmers, gaps
  }), [varieties, teaTypes, teaSeasons, sellers, farmers, gaps]);

  // データ取得の条件(enabled)を定義
  const shouldFetch = !!itemFilters.biddingDate && isInitialized;

  // データ取得フック
  const {
    data: responseData,
    isLoading: isItemsLoading,
    isFetching,
    error,
  } = useBiddingResultGetAllResultListQuery(
      itemFilters, 
      currentUser?.loginId ?? "",
      { enabled: shouldFetch } 
  );
  
  // 一括更新用のハンドラ
  const handleRefreshAll = async () => {
    // 1. 一覧データのキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddinginfo', 'allresult'] }); 
    
    // 2. ヘッダーの件数（CommonLayoutで使用しているキー）のキャッシュを無効化
    queryClient.invalidateQueries({ queryKey: ['biddingitemsstatusupdate'] });
  };

  const { data: favorites } = useGetFavoritesQuery(
  currentUser?.loginId ?? "", 
  { enabled: isBuyer }
  );

  // PDF取得用フックの呼び出し（enabled: falseにして手動実行に）
  const { refetch: fetchPdf, isFetching: isPdfLoading } = useBiddingResultPdfUrlQuery(itemFilters, {
    enabled: false,
  });

  const displayItems = useMemo(() => {
    if (!responseData || !responseData.items) return [];
    
    let items = responseData.items;

    // 「お気に入りのみ」絞り込み
    if (isBuyer && displayMode === 'favorites') {
      if (!favorites) return [];
      items = items.filter(item => 
        favorites.some(f => f.itemId === item.itemId && f.branchNumber === item.branchNumber)
      );
    }

    return items;
  }, [responseData, isBuyer, displayMode, favorites]);

  //----------------
  // Event Handlers
  //----------------
  
  // 入札日変更ハンドラ
  const handleDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedDate = e.target.value; 
    updateFiltersAndNavigate({
      ...itemFilters,
      biddingDate: selectedDate ? dayjs(selectedDate).format("YYYYMMDD") : ""
    });
  }, [itemFilters, updateFiltersAndNavigate]);

  // 出品区分（合体セレクトボックス）の変更ハンドラ
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
  if (selectedValue === "") {
    // 「すべて」が選ばれた場合、区分も地区も検索条件から外す
    updateFiltersAndNavigate({
      ...itemFilters,
      listingCategory: undefined, // ★ 2(通常入札)に固定せず、クリアする
      prefectureType: undefined, 
    });
    return;
  }

  const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
  updateFiltersAndNavigate({
    ...itemFilters,
    listingCategory: listingCategory,
    prefectureType: prefCategory, 
  });
  }, [itemFilters, updateFiltersAndNavigate]);

  // アコーディオン切替 (排他制御)
  const handleToggleDetail = useCallback((uniqueKey: string) => {
    setExpandedKey(prev => (prev === uniqueKey ? null : uniqueKey));
  }, []);

  // 表示対象(全件/入札済)変更ハンドラ
  const handleBiddingDisplayConditionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    updateFiltersAndNavigate({ 
      ...itemFilters, 
      biddingDisplayCondition: Number(e.target.value)
    });
  }, [itemFilters, updateFiltersAndNavigate]);

  // 直接印刷ハンドラの実装
  const handlePrintDirect = useCallback(async () => {
    if (isPdfLoading || isPrinting) return;
    
    try {
      setIsPrinting(true);
      
      const { data: pdfUrl } = await fetchPdf();
      if (!pdfUrl) {
        setIsPrinting(false);
        return;
      }
  
      // 既存の印刷用iframeがあれば削除
      const oldIframe = document.getElementById('pdf-print-iframe');
      if (oldIframe) document.body.removeChild(oldIframe);
  
      const iframe = document.createElement('iframe');
      iframe.id = 'pdf-print-iframe';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = pdfUrl;
      document.body.appendChild(iframe);
  
      iframe.onload = () => {
        // PDFの読み込み完了後、少し待ってから印刷ダイアログを起動
        setTimeout(() => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          
          // ダイアログが立ち上がったタイミングで「準備中...」を解除
          setIsPrinting(false);
          URL.revokeObjectURL(pdfUrl);
        }, 500);
      };
    } catch (err) {
      console.error(err);
      setIsPrinting(false);
    }
  }, [fetchPdf, isPdfLoading, isPrinting]);

  //----------------
  // Render
  //----------------
  const isPageLoading = isMastersLoading || isDateLoading || !isInitialized || (isItemsLoading && !responseData);

  // ページ全体のローディング（他画面と統一）
  if (isPageLoading) {
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center">
        <p className="text-lg text-gray-600">読み込み中...</p>
      </div>
    );
  }

  //【修正】エラー表示（型エラー解消 ＆ 他画面とデザイン統一）
  if (error) {
    const errorMessage = error instanceof Error ? error.message : "予期せぬエラーが発生しました";
    return (
      <div className="bg-white min-h-screen font-sans flex items-center justify-center p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">エラーが発生しました:</strong>
          <span className="block sm:inline"> {errorMessage}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-white min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
      <header className="mb-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-extrabold text-slate-800">落札結果の確認</h1>
                
                {/* 詳細検索ボタン */}
                <button
                    onClick={() => setIsFilterOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition border border-indigo-200"
                >
                    詳細検索
                </button>
            </div>

            {/* ヘッダー検索エリア */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 flex flex-wrap items-end gap-6">

                  {/* 1. 入札日 */}
                  <div className="w-full sm:w-48">
                      <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">入札日</label>
                      <input 
                          type="date" 
                          value={
                            itemFilters.biddingDate 
                              ? dayjs(String(itemFilters.biddingDate).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD") 
                              : ""
                          } 
                          onChange={handleDateChange}
                          className="w-full p-2 border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white 
                   appearance-none h-[42px] leading-tight safari-date-fix"
                      />
                  </div>
                  
                  {/* 2. 出品区分 */}
                  <div className="w-full sm:w-56">
                      <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
                      <select
                          disabled={isActualSeller || isFarmer || isJA} 
                          value={
                            (itemFilters.listingCategory && itemFilters.prefectureType)
                              ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                              : ""
                          } 
                          onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                          className={`w-full p-2 border border-slate-300 rounded-md ${
                            (isActualSeller || isFarmer || isJA) ? "bg-slate-100 text-slate-500" : "bg-white"
                          }`}
                      >
                          {CombinedCategoryOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                      </select>
                  </div>
                  
                  {/* 3. 買い手専用：表示切り替えオプション */}
                  {isBuyer && (
                    <>
                      {/* A. 入札状況 */}
                      <div className="w-full sm:w-auto">
                        <label className="block text-sm font-bold text-slate-700 mb-1">入札状況</label>
                        <div className="flex gap-4 bg-white px-3 h-[42px] items-center rounded-md border border-slate-300 shadow-sm">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="biddingDisplayCondition"
                              value={BiddingDisplayConditionConst.BIDDING}
                              checked={Number(itemFilters.biddingDisplayCondition) === BiddingDisplayConditionConst.BIDDING}
                              onChange={handleBiddingDisplayConditionChange}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className="text-sm font-medium text-slate-700">入札済</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="biddingDisplayCondition"
                              value={BiddingDisplayConditionConst.ALL}
                              checked={Number(itemFilters.biddingDisplayCondition) === BiddingDisplayConditionConst.ALL}
                              onChange={handleBiddingDisplayConditionChange}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className="text-sm font-medium text-slate-700">全件</span>
                          </label>
                        </div>
                      </div>
                  
                      {/* B. 表示対象 */}
                      <div className="w-full sm:w-auto">
                        <label className="block text-sm font-bold text-slate-700 mb-1">表示対象</label>
                        <div className="flex gap-4 bg-white px-3 h-[42px] items-center rounded-md border border-slate-300 shadow-sm">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="displayMode"
                              value="all"
                              checked={displayMode === 'all'}
                              onChange={() => setDisplayMode('all')}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className="text-sm font-medium text-slate-700">すべて</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="displayMode"
                              value="favorites"
                              checked={displayMode === 'favorites'}
                              onChange={() => setDisplayMode('favorites')}
                              className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            />
                            <span className="text-sm font-medium text-slate-700 whitespace-nowrap">お気に入りのみ</span>
                          </label>
                        </div>
                      </div>
                    </>
                  )}

                  {/* 4. 印刷ボタン：Farmer以外 */}
                  {!isFarmer && (
                  <div className="ml-auto">
                    <button
                      type="button"
                      disabled={isPdfLoading || isPrinting}
                      onClick={handlePrintDirect}
                      className="w-30 h-[42px] bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-md shadow-md transition-all duration-200 font-bold active:scale-95 whitespace-nowrap"
                    >
                      {isPdfLoading ? '準備中...' : '印刷プレビュー'}
                    </button>
                  </div>
                )}
              </div>
            </header>
        <main>
            {!shouldFetch ? (
                <div className="text-center py-10 text-gray-500">
                    入札日を選択してください。
                </div>
            ) : (responseData && !responseData.isSuccess) ? (
                <div className="text-center py-10 text-gray-500">
                    {responseData.message}
                </div>
            ) : (displayItems.length === 0) ? (
                <div className="text-center py-10 text-gray-500">
                    該当する落札情報がありません。
                </div>
            ) : (
                /* 一覧表示 */
                <div className="space-y-4">
                  {displayItems.map((item) => {
                    const uniqueKey = `${item.itemId}-${item.branchNumber}`;
                                    
                    // ★ ここでお気に入りかどうかを判定する
                    const isFavorite = favorites?.some(
                      (f) => f.itemId === item.itemId && f.branchNumber === item.branchNumber
                    ) ?? false;
                  
                    return (
                      <BiddingResultItemRow 
                        key={uniqueKey}
                        item={item} 
                        isExpanded={expandedKey === uniqueKey}
                        onToggle={() => handleToggleDetail(uniqueKey)}
                        isFavorite={isFavorite} // ★ Propで渡す
                        isSellerLayout={isSellerLayout}
                      />
                    );
                  })}
                </div>
            )}
        </main>

        {/* 検索ダイアログ */}
        <BiddingInputFilterDialog 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)} 
          masterData={masterDataForComponents}
          isSeller={isActualSeller} 
          isFarmer={isFarmer} // ★ 生産者フラグを渡す
          isAdminEditorOrViewer={isAdminEditorOrViewer} 
        />
      </div>
      <button
        onClick={handleRefreshAll}
        disabled={isFetching}
        className="fixed bottom-6 left-6 sm:bottom-10 sm:left-10 z-50 p-3 sm:p-3.5 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 hover:shadow-xl transition-all active:scale-95 disabled:bg-indigo-400 focus:outline-none focus:ring-4 focus:ring-indigo-300"
        title="最新の情報に更新"
      >
        <RefreshCw className={`w-5 h-5 sm:w-6 sm:h-6 ${isPdfLoading ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
};